import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { AuthConstants, AuthService } from '../../auth';
import { filter, Subscription } from 'rxjs';
import { User } from '../../auth';
import { TranslocoDirective } from "@jsverse/transloco";
import { EmailInputFormComponent } from '@frontend/shared';
import { AlertModule } from 'ngx-bootstrap/alert';
import { LoaderGlobeAnimationDetailComponent } from '../../loaders';

@Component({
  selector: 'user-account-details-update',
  templateUrl: './user-account-details-update.component.html',
  styleUrls: ['./user-account-details-update.component.scss'],
  imports: [
    ReactiveFormsModule,
    TranslocoDirective,
    EmailInputFormComponent,
    AlertModule,
    LoaderGlobeAnimationDetailComponent,
  ],
})
export class UserAccountDetailsUpdateComponent implements OnInit, OnDestroy {

  user = signal<User | null | undefined>(undefined);
  loading: boolean = false;
  error: string | null = null; // the translationKey
  detailsForm : string; // 'email' or 'password'
  routeSubscription: Subscription;
  userSubscription: Subscription;
  showWarningAlert: boolean = false;
  showPasswordSuccessAlert: boolean = false;
  showCheckInboxText: boolean = false; // Adds some extra text in the password success alert
  passwordSuccessTranslationKey: string = ''; // 'authentication.password_changed'
  oldPasswordForgotten: boolean = false;
  changePasswordForm: FormGroup;
  changePasswordSubscription: Subscription;
  forgotPasswordSubscription: Subscription;
  authConstants: any;

  constructor(
    private router: Router,
    private authService: AuthService,
    ) {
      this.authConstants = AuthConstants;
    }

    requestNewPasswordLink (){
      if (!this.user()?.email) {
        return;
      }
      this.loading = true;
      this.showPasswordSuccessAlert = false;
      this.forgotPasswordSubscription = this.authService.forgotPassword(this.user()?.email).subscribe(
        () => {
          this.loading = false;
          this.doShowPasswordSuccessAlert('authentication.forgot_password_success_email',true);
        },
        errorMessage => {
          this.loading = false;
          this.error = errorMessage;
        }
      );
    };


    changeEmail (newEmail: string){
      this.loading = true;
      this.authService.changeEmail(newEmail).subscribe(
        () => {
          this.loading = false;
        },
        errorMessage => {
          this.loading = false;
          const translationKey = errorMessage?.message ? errorMessage.message : errorMessage;
          this.error = translationKey == 'authentication.already_registered_log_in'
            ? 'authentication.already_registered'
            : translationKey;
        });
    };

  doChangeEmail (newEmail:string){
    this.changeEmail(newEmail);
  }
  // The error keys, the field styling, the messages and the submit button all derive from these
  // getters, which read the current values, so they cannot disagree. Do not add Angular validators.
  private formatErrorKey(value: string | null): string | null {
    if (!value) {
      return 'common.required';
    }
    if (value.length > this.authConstants.passwordMaxLength) {
      return 'common.too_long';
    }
    if (!this.authConstants.passwordRegexPattern.test(value)) {
      return 'authentication.password_min_length';
    }
    return null;
  }

  get oldPasswordErrorKey(): string | null {
    return this.changePasswordForm.controls.oldPassword.value ? null : 'common.required';
  }

  get passwordErrorKey(): string | null {
    return this.formatErrorKey(this.changePasswordForm.controls.password.value);
  }

  get repeatPasswordErrorKey(): string | null {
    const password = this.changePasswordForm.controls.password.value;
    const repeatPassword = this.changePasswordForm.controls.repeatPassword.value;
    const formatErrorKey = this.formatErrorKey(repeatPassword);
    if (formatErrorKey) {
      return formatErrorKey;
    }
    return password && password !== repeatPassword
      ? 'authentication.password_confirmation_error'
      : null;
  }

  // An untouched empty field is neutral, not invalid; 'dirty' means the user has typed in it.
  get showOldPasswordError(): boolean {
    return !this.oldPasswordForgotten && this.changePasswordForm.controls.oldPassword.dirty && !!this.oldPasswordErrorKey;
  }

  get isOldPasswordValid(): boolean {
    return this.changePasswordForm.controls.oldPassword.dirty && !this.oldPasswordErrorKey;
  }

  get showPasswordError(): boolean {
    return this.changePasswordForm.controls.password.dirty && !!this.passwordErrorKey;
  }

  get isPasswordValid(): boolean {
    return this.changePasswordForm.controls.password.dirty && !this.passwordErrorKey;
  }

  get showRepeatPasswordError(): boolean {
    return this.changePasswordForm.controls.repeatPassword.dirty && !!this.repeatPasswordErrorKey;
  }

  get isRepeatPasswordValid(): boolean {
    return this.changePasswordForm.controls.repeatPassword.dirty && !this.repeatPasswordErrorKey;
  }

  get canSubmit(): boolean {
    return !this.oldPasswordErrorKey && !this.passwordErrorKey && !this.repeatPasswordErrorKey;
  }

  doShowPasswordSuccessAlert (translationKey: string, showCheckInboxText){
    this.showPasswordSuccessAlert = true;
    this.showCheckInboxText = showCheckInboxText;
    this.passwordSuccessTranslationKey = translationKey; //'authentication.password_changed';
    setTimeout(()=>{
      this.showPasswordSuccessAlert = false;
    },10000);
  }

  changePassword() {
    if (!this.canSubmit) {
      return;
    }
    this.loading = true;
    this.error = null;

    this.changePasswordSubscription = this.authService.changePassword(this.changePasswordForm.get('oldPassword').value,this.changePasswordForm.get('password').value).subscribe(
      () => {
        this.loading = false;
        this.changePasswordForm.reset();
        this.doShowPasswordSuccessAlert('authentication.password_changed',false);
      },
      errorMessage => {
        this.loading = false;
        this.error = errorMessage;
      });
  }


  generateForm (){
    this.changePasswordForm = new FormGroup({
      oldPassword: new FormControl<string | null>(null),
      password: new FormControl<string | null>(null),
      repeatPassword: new FormControl<string | null>(null),
    });
  }
  handleRouteChange(): void {

    const fullUrlParts = this.router.routerState.snapshot.url.toLowerCase().split('/').filter(s => s);

    this.detailsForm = fullUrlParts[1];
    if(this.detailsForm === 'password') {
      this.generateForm();
    }
  }

  ngOnInit(): void {
    this.userSubscription = this.authService.user.subscribe((user) => {
      this.user.set(user);
    });
    // Ensure we have the editable profile payload (including email fields) for this page.
    this.authService.getUser(true).subscribe();

    this.routeSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.handleRouteChange();
      });
      this.handleRouteChange();
  }

  ngOnDestroy(): void {
    // Don't forget to unsubscribe to prevent memory leaks
    this.routeSubscription?.unsubscribe();
    this.userSubscription?.unsubscribe();
    this.changePasswordSubscription?.unsubscribe();
    this.forgotPasswordSubscription?.unsubscribe();
  }

}
